import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

const HeroCanvas = lazy(() => import('../hero/HeroCanvas'))

class HeroBoundary extends Component<{ children: ReactNode; onFailure: () => void }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() { return { failed: true } }
  componentDidCatch() { this.props.onFailure() }
  render() { return this.state.failed ? null : this.props.children }
}

/** The original artwork is the immediate, permanent fallback for this optional hero. */
export default function HomeHero() {
  const host = useRef<HTMLDivElement>(null)
  const pointer = useRef({ x: 0, y: 0 })
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  const [visible, setVisible] = useState(() => !document.hidden)
  const [modelUrl, setModelUrl] = useState<string>()
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const [progress, setProgress] = useState(0)
  const onReady = useCallback(() => setReady(true), [])
  const onError = useCallback(() => { setFailed(true); setReady(false) }, [])
  const onProgress = useCallback((percent: number) => setProgress(Math.floor(percent / 10) * 10), [])

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updateMotion = () => {
      setReducedMotion(query.matches)
      if (query.matches) { setReady(false); setProgress(0) }
    }
    const updateVisibility = () => setVisible(!document.hidden)
    query.addEventListener('change', updateMotion)
    document.addEventListener('visibilitychange', updateVisibility)
    return () => {
      query.removeEventListener('change', updateMotion)
      document.removeEventListener('visibilitychange', updateVisibility)
    }
  }, [])

  useEffect(() => {
    if (reducedMotion || failed || modelUrl || !visible) return
    const abort = new AbortController()
    const load = async () => {
      try {
        const response = await fetch('/assets/hero/manifest.json', { signal: abort.signal })
        if (!response.ok) throw new Error('Hero unavailable')
        const manifest = await response.json()
        const url: unknown = manifest.runtime?.url
        if (typeof url !== 'string' || !/^\/assets\/hero\/[a-f0-9]{64}\.glb$/.test(url)) throw new Error('Hero unavailable')
        if (!abort.signal.aborted) setModelUrl(url)
      } catch {
        if (!abort.signal.aborted) onError()
      }
    }
    // Leave the initial DOM paint and the visit button independent of WebGL.
    const timer = window.setTimeout(() => void load(), 250)
    return () => { window.clearTimeout(timer); abort.abort() }
  }, [reducedMotion, failed, modelUrl, visible, onError])

  useEffect(() => {
    if (reducedMotion) { pointer.current = { x: 0, y: 0 }; return }
    const stage = host.current?.closest('main')
    if (!stage) return
    const move = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return
      const rect = stage.getBoundingClientRect()
      pointer.current.x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1))
      pointer.current.y = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1))
    }
    const reset = () => { pointer.current.x = 0; pointer.current.y = 0 }
    stage.addEventListener('pointermove', move, { passive: true })
    stage.addEventListener('pointerleave', reset)
    return () => { stage.removeEventListener('pointermove', move); stage.removeEventListener('pointerleave', reset) }
  }, [reducedMotion])

  const enhanced = !failed && !reducedMotion && Boolean(modelUrl)
  return <div ref={host} className={`home-visual home-hero${ready && enhanced ? ' home-hero--ready' : ''}`}>
    <figure className="unity-art home-art home-hero__poster"><img src="/assets/unity/art-14.png" alt="黑底上展开半透明白翼的复原标本" fetchPriority="high" /></figure>
    {enhanced && modelUrl && <div className="home-hero__canvas" aria-hidden="true">
      <HeroBoundary onFailure={onError}><Suspense fallback={null}><HeroCanvas modelUrl={modelUrl} frontRotation={-Math.PI / 2} pointer={pointer} motion={!reducedMotion} visible={visible} onReady={onReady} onError={onError} onProgress={onProgress} /></Suspense></HeroBoundary>
    </div>}
    {enhanced && !ready && <span className="home-hero__loading" role="status">加载立体展品{progress > 0 ? ` · ${progress}%` : '…'}</span>}
  </div>
}
